import React, { useEffect, useRef, useState } from "react";

/**
 * TypingText (robusto)
 *
 * Props:
 * - text: string to display
 * - speed: ms per character (default 15). Minimum enforced to 5ms.
 * - active: boolean -> when true starts/continues the animation
 * - onComplete: () => void called only when the animation reaches the end naturally
 * - startEmpty: boolean (default true). If false, component mounts showing full text.
 * - finishOnCancel: boolean (default true). If active becomes false while animating,
 *                   show full text immediately (but do NOT call onComplete).
 */
export const TypingText = ({
  text = "",
  speed = 15,
  active = false,
  onComplete,
  startEmpty = true,
  finishOnCancel = true,
}) => {
  const [displayed, setDisplayed] = useState(startEmpty ? "" : text);

  // refs to hold mutable state without re-renders
  const indexRef = useRef(startEmpty ? 0 : text.length);
  const timeoutRef = useRef(null);
  const completedRef = useRef(false); // true only when natural completion happened
  const mountedRef = useRef(true);
  const lastTextRef = useRef(text);

  // keep mounted flag to avoid setState after unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // When text changes externally, reset so it can re-animate
  useEffect(() => {
    if (lastTextRef.current !== text) {
      lastTextRef.current = text;
      completedRef.current = false;
      indexRef.current = startEmpty ? 0 : text.length;
      if (mountedRef.current) setDisplayed(startEmpty ? "" : text);
    }
    // include startEmpty intentionally
  }, [text, startEmpty]);

  useEffect(() => {
    // If already completed naturally, ensure displayed shows full text and do nothing else
    if (completedRef.current) {
      if (mountedRef.current) setDisplayed(text);
      return;
    }

    // If not active: cancel any in-flight timeout and optionally finish (show full text)
    if (!active) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (finishOnCancel) {
        // Do not mark as completedRef (so future active true will re-animate if text changed)
        if (mountedRef.current) setDisplayed(text);
        indexRef.current = text.length;
      }
      return;
    }

    // Start / resume animation: always reset index to 0 to start fresh typing
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    indexRef.current = 0;
    if (mountedRef.current) setDisplayed("");

    // recursive tick using setTimeout — safer than setInterval
    const tick = () => {
      indexRef.current += 1;
      if (!mountedRef.current) return;

      setDisplayed(text.slice(0, indexRef.current));

      if (indexRef.current >= text.length) {
        completedRef.current = true;
        timeoutRef.current = null;
        // call onComplete exactly once, only when completed naturally
        if (typeof onComplete === "function") {
          try {
            onComplete();
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error("TypingText onComplete callback error:", e);
          }
        }
        return;
      }

      // schedule next char
      timeoutRef.current = window.setTimeout(
        tick,
        Math.max(5, speed) // enforce small minimum
      );
    };

    timeoutRef.current = window.setTimeout(tick, Math.max(5, speed));

    // cleanup for this effect
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };   
  }, [active, text, speed, onComplete, finishOnCancel]);

  // Render inline — keep it simple; parent can style with typography props
  return (
    <span aria-live="polite" style={{ whiteSpace: "pre-wrap" }}>
      {displayed}
    </span>
  );
};
